import Breadcrumb from "components/Common/Breadcrumb"
import { useEffect, useMemo, useState } from "react"
import { FaEdit, FaPlus, FaSave } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Badge,
  Button,
  Card,
  CardBody,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import React from "react"
import Swal from "sweetalert2"
import Select from "react-select"
import {
  loadContacts,
  loadGroups,
  saveGroups,
} from "utils/waContacts"

const EMPTY_FORM = { groupName: "", contactIds: [] }

// Solid backgrounds for the members dropdown (app CSS leaves it transparent)
const MEMBER_SELECT_STYLES = {
  control: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    minHeight: 38,
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#cb9019"
      : state.isFocused
        ? "#faf3e2"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#263a5b",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#faf3e2",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#6b5a33",
  }),
}

const WhatsAppGroups = () => {
  document.title = "WhatsApp Groups | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(() => loadGroups())
  const [contacts, setContacts] = useState(() => loadContacts())
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  // TODO: gate with backend permission id when available (showing openly for now)

  useEffect(() => {
    saveGroups(data)
  }, [data])

  const contactOptions = useMemo(
    () =>
      contacts.map(c => ({
        value: c.id,
        label: `${c.contactName} (${c.phoneNumber})`,
      })),
    [contacts]
  )

  const getContactName = id =>
    contacts.find(c => String(c.id) === String(id))?.contactName ||
    "Unknown"

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(
      item =>
        item?.groupName?.toLowerCase().includes(search) ||
        item?.contactIds?.some(id =>
          getContactName(id).toLowerCase().includes(search)
        )
    )
  }, [data, searchString, contacts])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setContacts(loadContacts())
    setSelectedGroup(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setContacts(loadContacts())
    setSelectedGroup({
      ...item,
      contactIds: [...(item.contactIds || [])],
    })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedGroup?.groupName?.trim()) {
      newErrors.groupName = "Group name is required"
    }
    if (!selectedGroup?.contactIds?.length) {
      newErrors.contactIds = "Select at least one contact"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  const handleSubmit = () => {
    if (!validate()) return

    if (selectedGroup?.id) {
      setData(prev =>
        prev.map(item =>
          item.id === selectedGroup.id
            ? {
                ...item,
                groupName: selectedGroup.groupName.trim(),
                contactIds: selectedGroup.contactIds,
              }
            : item
        )
      )
      Swal.fire({
        title: "Group Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const duplicate = data.some(
        d =>
          d.groupName?.toLowerCase() ===
          selectedGroup.groupName.trim().toLowerCase()
      )
      if (duplicate) {
        setErrors(prev => ({
          ...prev,
          groupName: "A group with this name already exists",
        }))
        return
      }
      const newItem = {
        id: Date.now(),
        groupName: selectedGroup.groupName.trim(),
        contactIds: selectedGroup.contactIds,
        createdAt: new Date().toISOString(),
      }
      setData(prev => [newItem, ...prev])
      Swal.fire({
        title: "Group Created Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedGroup(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "whatsapp-groups" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.groupName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Group Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "whatsapp-groups" when API is ready
      }
    })
  }

  const renderMembers = ids => {
    if (!ids?.length) return <span className="text-muted">-</span>
    const visible = ids.slice(0, 3)
    const remaining = ids.length - visible.length
    return (
      <div className="d-flex flex-wrap gap-1">
        {visible.map(id => (
          <Badge
            key={id}
            pill
            color="primary"
            className="fw-normal"
            style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
          >
            {getContactName(id)}
          </Badge>
        ))}
        {remaining > 0 && (
          <Badge
            pill
            color="secondary"
            className="fw-normal"
            style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
          >
            +{remaining} more
          </Badge>
        )}
      </div>
    )
  }

  return (
    <div className="page-content">
      <Breadcrumb title="WhatsApp" breadcrumbItem="Groups" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-nowrap">
            <div className="d-flex gap-2 align-items-center flex-nowrap flex-grow-1">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 pe-5 flex-shrink-0"
                style={{ width: "90px" }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4 flex-grow-1"
                style={{ maxWidth: "320px" }}
                placeholder="Search Groups"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            <Button
              className="btn-blue rounded-4 px-3 flex-shrink-0 text-nowrap"
              onClick={openAddModal}
            >
              <FaPlus className="me-2" />
              Group
            </Button>
          </div>

          {/* Table */}
          <Table bordered responsive className="align-middle">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "70px" }}>
                  #
                </th>
                <th style={{ minWidth: "200px" }}>Group Name</th>
                <th className="text-center" style={{ width: "110px" }}>
                  Members
                </th>
                <th style={{ minWidth: "220px" }}>Contacts</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Groups found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{start + index + 1}</td>
                    <td>
                      <span className="fw-bold">{item.groupName}</span>
                    </td>
                    <td className="text-center">
                      {item.contactIds?.length || 0}
                    </td>
                    <td>{renderMembers(item.contactIds)}</td>
                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        title="Edit group"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>
                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        title="Delete group"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedGroup?.id ? "Edit" : "Add"} Group
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>
              Group Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. Job Alerts"
              value={selectedGroup?.groupName || ""}
              invalid={!!errors.groupName}
              onChange={e =>
                setSelectedGroup(prev => ({
                  ...prev,
                  groupName: e.target.value,
                }))
              }
            />
            {errors.groupName && (
              <FormFeedback className="d-block">
                {errors.groupName}
              </FormFeedback>
            )}
          </div>

          <div>
            <Label>
              Contacts <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={contactOptions}
              placeholder={
                contactOptions.length
                  ? "Select contacts"
                  : "No contacts yet — add some in Contacts first"
              }
              styles={MEMBER_SELECT_STYLES}
              value={contactOptions.filter(opt =>
                selectedGroup?.contactIds?.map(String).includes(String(opt.value))
              )}
              onChange={selected =>
                setSelectedGroup(prev => ({
                  ...prev,
                  contactIds: (selected || []).map(opt => opt.value),
                }))
              }
              className={errors.contactIds ? "is-invalid" : ""}
            />
            {errors.contactIds && (
              <div className="invalid-feedback d-block">
                {errors.contactIds}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave className="me-1" /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel className="me-1" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default WhatsAppGroups
